const BASE_URL = import.meta.env.VITE_BACKEND_HOST || window.location.origin;

const SCHEME_URL_AUTHORITY =
  // eslint-disable-next-line no-control-regex -- Inspect leading URL controls before browser normalization.
  /^[\u0000-\u0020]*([a-z][a-z0-9+.-]*):([\\/]*)([^\\/]*)(?:[\\/]|$)/i;
const PROTOCOL_RELATIVE_AUTHORITY =
  // eslint-disable-next-line no-control-regex -- Inspect leading URL controls before browser normalization.
  /^[\u0000-\u0020]*([\\/]{2,})([^\\/]*)(?:[\\/]|$)/;
const INVALID_URI_CHARACTERS = /[^A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=%]/;
const INVALID_PERCENT_ENCODING = /%(?![0-9a-f]{2})/i;
const RAW_DOT_PATH_SEGMENT = /(?:^|[\\/])(?:\.|%2e)(?:\.|%2e)?(?=[\\/]|$)/i;
const SUPPORTED_STORAGE_SCHEMES = new Set(["http", "https"]);

interface RawUrlAuthority {
  authority: string;
  scheme: string | null;
  slashCount: number;
}

function getRawUrlAuthority(originalUrl: string): RawUrlAuthority | null {
  const schemeMatch = originalUrl.match(SCHEME_URL_AUTHORITY);
  if (schemeMatch) {
    return {
      authority: schemeMatch[3],
      scheme: schemeMatch[1].toLowerCase(),
      slashCount: schemeMatch[2].length,
    };
  }

  const protocolRelativeMatch = originalUrl.match(PROTOCOL_RELATIVE_AUTHORITY);
  return protocolRelativeMatch
    ? {
        authority: protocolRelativeMatch[2],
        scheme: null,
        slashCount: protocolRelativeMatch[1].length,
      }
    : null;
}

function hasInvalidRawPort(authority: string): boolean {
  const hostWithPort = authority.slice(authority.lastIndexOf("@") + 1);
  const parts = hostWithPort.split(":");
  if (parts.length === 1) return false;
  if (parts.length !== 2) return true;

  const port = parts[1];
  return port !== "" && (!/^\d+$/.test(port) || Number(port) > 65_535);
}

function hasMalformedUrlSyntax(
  originalUrl: string,
  rawAuthority: RawUrlAuthority | null,
): boolean {
  const queryStart = originalUrl.indexOf("?");
  const fragmentStart = originalUrl.indexOf("#");
  const hasQueryBeforeFragment =
    queryStart !== -1 && (fragmentStart === -1 || queryStart < fragmentStart);
  const beforeQuery = hasQueryBeforeFragment
    ? originalUrl.slice(0, queryStart)
    : originalUrl;
  const fragment = fragmentStart === -1 ? "" : originalUrl.slice(fragmentStart);
  const hasBracketsOutsideQuery =
    /[[\]]/.test(beforeQuery) || /[[\]]/.test(fragment);
  const hasMultipleFragments = (originalUrl.match(/#/g) ?? []).length > 1;
  const pathAndAuthority = originalUrl.split(/[?#]/, 1)[0];

  return (
    INVALID_URI_CHARACTERS.test(originalUrl) ||
    hasBracketsOutsideQuery ||
    hasMultipleFragments ||
    INVALID_PERCENT_ENCODING.test(originalUrl) ||
    RAW_DOT_PATH_SEGMENT.test(pathAndAuthority) ||
    (rawAuthority !== null &&
      ((rawAuthority.scheme !== null &&
        !SUPPORTED_STORAGE_SCHEMES.has(rawAuthority.scheme)) ||
        rawAuthority.slashCount !== 2 ||
        rawAuthority.authority.includes("%") ||
        (rawAuthority.authority.match(/@/g) ?? []).length > 1 ||
        hasInvalidRawPort(rawAuthority.authority)))
  );
}

export function getConfiguredStorageBucket(): string | null {
  try {
    const config = JSON.parse(import.meta.env.VITE_FIREBASE_CONFIG as string);
    return typeof config.storageBucket === "string"
      ? config.storageBucket
      : null;
  } catch {
    return null;
  }
}

// Only known, well-formed URLs from this environment may lose their bucket
// when rewritten to a local proxy. Everything else stays untouched.
export function getStorageProxyUrl(originalUrl: string): string {
  if (typeof originalUrl !== "string" || !originalUrl) return originalUrl;
  const configuredBucket = getConfiguredStorageBucket();
  const rawAuthority = getRawUrlAuthority(originalUrl);
  if (
    !configuredBucket ||
    !rawAuthority ||
    hasMalformedUrlSyntax(originalUrl, rawAuthority)
  )
    return originalUrl;

  try {
    const url = new URL(originalUrl, "https://storage-url.invalid");
    // A single DNS terminal dot is supported; multiple dots are malformed.
    const hostname = url.hostname.replace(/\.$/, "");
    let match: RegExpMatchArray | null;
    let proxyPath: string;
    if (hostname === "firebasestorage.googleapis.com") {
      match = url.pathname.match(/^\/v0\/b\/([^/]+)\/o\/(.+)$/);
      if (!match) return originalUrl;
      proxyPath = `/o/${match[2]}`;
    } else if (hostname === "storage.googleapis.com") {
      match = url.pathname.match(/^\/([^/]+)\/(.+)$/);
      if (!match) return originalUrl;
      proxyPath = `/o1/${match[2]}`;
    } else {
      return originalUrl;
    }
    if (match[1] !== configuredBucket) return originalUrl;
    const proxyUrl = new URL(proxyPath, BASE_URL);
    proxyUrl.search = url.search;
    return proxyUrl.toString();
  } catch {
    return originalUrl;
  }
}
